/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchEmployees } from "../../store/slices/employeeSlice";
import { fetchCompaniesByHierarchy } from "../../store/slices/companySlice";
import { importEmployees } from "../../store/slices/employeeImportSlice";
import EmployeeUploadModal from "../components/EmployeeUploadModal";
import { SearchBar } from "../components/SearchBar";
import { fetchBranchesByHierarchy } from "../../store/slices/branchSlice";
import { fetchDepartments } from "../../store/slices/departmentSlice";

const EmployeeTable = ({ filters, setFilters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* ========================================================================== */
  /* 1. REDUX & LOCAL STORAGE SELECTORS                                        */
  /* ========================================================================== */
  const employeeState = useSelector((state) => state.employee);
  const employeeListData = useMemo(() => {
    if (!employeeState.list) return [];
    if (Array.isArray(employeeState.list)) return employeeState.list;
    return employeeState.list.data || [];
  }, [employeeState.list]);
  const currentData = employeeListData;

  const { list: allOrgs = [] } = useSelector((state) => state.companyTypeList);
  const { list: allBranches = [] } = useSelector((state) => state.branch);

  const storedCompanyId = localStorage.getItem("companyId");
  const storedBranchId = localStorage.getItem("branchId");

  /* ========================================================================== */
  /* 2. ALL LOCAL COMPONENT STATES (Moved up here to prevent hoisting errors)  */
  /* ========================================================================== */
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "firstName",
    direction: "asc",
  });

  const isMultipleBranches = allBranches.length > 1;

  const branchOptions = isMultipleBranches
    ? [
        { value: storedBranchId, label: "All Branch" },
        ...allBranches
          .filter(
            (branch) =>
              branch.id !== "All Branch" &&
              branch.id !== storedBranchId &&
              !/all/i.test(branch.branchName || branch.name)
          )
          .map((branch) => ({
            value: branch.id,
            label: branch.branchName || branch.name,
          })),
      ]
    : allBranches.map((branch) => ({
        value: branch.id,
        label: branch.branchName || branch.name,
      }));

  const isMultipleCompanies = allOrgs.length > 1;

  const companyOption = isMultipleCompanies
    ? [
        { value: "", label: "All Company" },
        ...allOrgs.map((org) => ({
          value: org.id,
          label: org.companyName || org.name || org,
        })),
      ]
    : allOrgs.map((org) => ({
        value: org.id,
        label: org.companyName || org.name || org,
      }));

  const [selectedCompanyId, setSelectedCompanyId] = useState(
    filters.company || storedCompanyId || ""
  );
  const [selectedBranchId, setSelectedBranchId] = useState(
    filters.branch || storedBranchId || ""
  );
  
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [preview, setPreview] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const status = employeeState.status;

  const serverMeta = useMemo(() => {
    const nestedMeta = employeeState.meta;
    if (nestedMeta && typeof nestedMeta === "object") {
      return {
        total: Number(nestedMeta?.total ?? 0),
        totalPages: Number(nestedMeta?.totalPages ?? 1),
        page: Number(nestedMeta?.page ?? 1),
      };
    }
    const fallbackLength = Array.isArray(employeeState.list)
      ? employeeState.list.length
      : employeeListData?.length ?? 0;
    return {
      total: fallbackLength,
      totalPages: Math.ceil(fallbackLength / itemsPerPage) || 1,
      page: 1,
    };
  }, [employeeState, employeeListData?.length, itemsPerPage]);

  const totalPages = serverMeta.totalPages;
  const totalItems = serverMeta.total;
  const isLoading = status === "loading" || isImporting;

  const toCamelCase = (str) => {
    if (!str) return "Active";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getStatusStyle = (empStatus) => {
    const s = empStatus?.toLowerCase();
    if (s === "active" || s === "complete")
      return { bg: "bg-success", text: "text-success", border: "border-success" };
    if (["inactive", "resigned", "terminated", "awol"].includes(s))
      return { bg: "bg-danger", text: "text-danger", border: "border-danger" };
    if (["incomplete", "separated"].includes(s))
      return { bg: "bg-warning", text: "text-warning", border: "border-warning" };
    return { bg: "bg-secondary", text: "text-secondary", border: "border-secondary" };
  };

  // Sync company/branch state if external filters change (e.g. Back button)
  useEffect(() => {
    if (filters.company !== undefined && filters.company !== selectedCompanyId) {
      setSelectedCompanyId(filters.company);
    }
    if (filters.branch !== undefined && filters.branch !== selectedBranchId) {
      setSelectedBranchId(filters.branch);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Fetch employees ONLY when `filters`, `currentPage`, `itemsPerPage`, or company changes
  useEffect(() => {
    const cleanFilters = {};

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (
        value !== undefined &&
        value !== null &&
        value !== "All" &&
        value !== "both" &&
        String(value).trim() !== ""
      ) {
        const trimmedValue = String(value).trim();
        if (key === "branch") cleanFilters.branchId = trimmedValue;
        else if (key === "company") cleanFilters.companyId = trimmedValue;
        else if (key === "client") cleanFilters.clientId = trimmedValue;
        else if (key === "role") cleanFilters.roleId = trimmedValue;
        else cleanFilters[key] = trimmedValue;
      }
    });

    const fetchPayload = {
      ...cleanFilters,
      companyId: selectedCompanyId || storedCompanyId,
      page: currentPage,
      limit: itemsPerPage,
    };

    dispatch(fetchEmployees(fetchPayload));
  }, [dispatch, filters, currentPage, itemsPerPage, selectedCompanyId, storedCompanyId]);

  useEffect(() => {
    if (storedCompanyId) {
      dispatch(fetchCompaniesByHierarchy({ companyId: storedCompanyId }));
      dispatch(fetchDepartments({ companyId: storedCompanyId }));
    }
  }, [dispatch, storedCompanyId]);

  useEffect(() => {
    dispatch(fetchBranchesByHierarchy({ branchId: storedBranchId }));
  }, [dispatch, storedBranchId]);

  const handleSearch = (searchValues) => {
    setCurrentPage(1);

    const activeFilters = {
      ...filters,
      ...searchValues,
      company: searchValues.company !== undefined ? searchValues.company : (selectedCompanyId || filters.company),
      branch: searchValues.branch !== undefined ? searchValues.branch : (selectedBranchId || filters.branch),
      assignmentType: searchValues.assignmentType !== undefined ? searchValues.assignmentType : (searchValues.assignmentType || filters.assignmentType)
    };

    // Update internal structure identifiers if they changed
    if (activeFilters.company !== undefined) setSelectedCompanyId(activeFilters.company);
    if (activeFilters.branch !== undefined) setSelectedBranchId(activeFilters.branch);

    // This updates the parent state and triggers URL params updates
    if (setFilters) {
      setFilters(activeFilters);
    }
  };

  const handlePageChange = useCallback(
    (targetPage, overrideLimit = null) => {
      setCurrentPage(targetPage);
      if (overrideLimit !== null) {
        setItemsPerPage(overrideLimit);
      }
    },
    []
  );

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key)
      return (
        <i
          className="bi bi-arrow-down-up ms-2 opacity-25"
          style={{ fontSize: "0.6rem" }}
        ></i>
      );
    return sortConfig.direction === "asc" ? (
      <i
        className="bi bi-sort-down ms-2 text-primary"
        style={{ fontSize: "0.7rem" }}
      ></i>
    ) : (
      <i
        className="bi bi-sort-up ms-2 text-primary"
        style={{ fontSize: "0.7rem" }}
      ></i>
    );
  };

  const handleImport = async (file) => {
    try {
      const res = await dispatch(
        importEmployees({ file, companyId: selectedCompanyId, preview })
      ).unwrap();
      handlePageChange(currentPage);
      return res;
    } catch (err) {
      return err;
    }
  };

  return (
    <div className="w-100 position-relative">
      <style>{`
        .custom-table-header th { 
          background-color: #1e293b !important; color: #ffffff !important; 
          cursor: pointer; user-select: none; transition: background 0.2s;
          font-size: 0.7rem !important; font-weight: 700 !important; 
          letter-spacing: 0.05em !important; text-transform: uppercase !important;
        }
        .custom-table-header th:hover { background-color: #334155 !important; }
        .emp-name-text { font-size: 0.85rem !important; font-weight: 700 !important; color: #1e293b; }
        .emp-sub-text { font-size: 0.75rem !important; color: #64748b; }
        .table-cell-text { font-size: 0.8rem !important; color: #334155; }
        .badge-custom { 
          font-size: 0.65rem !important; font-weight: 700 !important; 
          letter-spacing: 0.02em !important; padding: 6px 12px !important;
        }
        .pagination-container { background: rgba(255, 255, 255, 0.4); border-top: 1px solid rgba(0,0,0,0.05); padding: 1rem 1.5rem; }
        .page-link-custom { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px !important; margin: 0 3px; font-size: 0.75rem; font-weight: 600; color: #64748b; cursor: pointer; background: transparent; transition: all 0.2s; border: 1px solid rgba(0,0,0,0.1); }
        .page-link-custom.active { background: #0f172a !important; color: #fff !important; border-color: #0f172a !important; }
        .filter-input, .status-select { border: 1px solid rgba(0,0,0,0.1); border-radius: 8px; font-size: 0.8rem !important; padding: 10px 14px; background: rgba(255,255,255,0.7); backdrop-filter: blur(5px); outline: none; }
        .items-select { border: 1px solid rgba(0,0,0,0.1); border-radius: 6px; padding: 4px 8px; outline: none; color: #334155; font-weight: 500; }
      `}</style>

      {isLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(10px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="spinner-border text-primary"></div>
        </div>
      )}

      <div className={isLoading ? "blur-content" : ""}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1
              className="fw-bold text-dark m-0"
              style={{ letterSpacing: "-1.5px" }}
            >
              Employees
            </h1>
            <p className="text-muted small mb-0">Manage employee records</p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary px-4 border-0"
              style={{
                borderRadius: "8px",
                backgroundColor: "#0f172a",
                fontWeight: "600",
                fontSize: "0.85rem",
              }}
              onClick={() => navigate("/employee/new")}
            >
              <i className="bi bi-plus-lg me-2"></i>Add New
            </button>
            <button
              className="btn btn-outline-secondary px-4 bg-white shadow-sm"
              style={{
                borderRadius: "8px",
                fontSize: "0.85rem",
                fontWeight: "600",
                border: "1px solid rgba(0,0,0,0.1)",
              }}
              onClick={() => setShowUploadModal(true)}
            >
              <i className="bi bi-file-earmark-arrow-up me-2"></i>Import
            </button>
          </div>
        </div>

        {/* SearchBar lets user type freely without triggering fetching until onSearch is clicked */}
<SearchBar
          filters={filters}
          setFilters={setFilters}
          fields={[
            { key: "search", label: "Name", type: "text", placeholder: "Search Name..." },
            { key: "department", label: "Department", type: "text", placeholder: "Search Dept..." },
            { key: "branch", label: "Branch", defaultValue: storedBranchId, type: "select", options: branchOptions },
            { key: "company", label: "Company", defaultValue: "All", type: "select", options: companyOption },
            {
              key: "status",
              label: "Status",
              type: "select",
              defaultValue: "All",
              options: [
                "All",
                "Active",
                "Incomplete",
                "Validated",
                "Resigned",
                "Deceased",
                "Notice period",
                "Terminated",
                "Awol",
              ],
            },
                        {
              key: "assignmentType",
              label: "Assignment Type",
              type: "select",
              defaultValue: "All",
              options: [
                "All",
                "Internal",
                "Deployed",
              ],
            },
          ]}
          onSearch={handleSearch}
        ></SearchBar>

        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(12px)",
            borderRadius: "16px",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  <th
                    className="py-3 ps-4 border-0"
                    onClick={() => requestSort("firstName")}
                  >
                    EMPLOYEE NAME {getSortIcon("firstName")}
                  </th>
                  <th
                    className="py-3 border-0"
                    onClick={() => requestSort("department")}
                  >
                    DEPARTMENT {getSortIcon("department")}
                  </th>
                  <th
                    className="py-3 border-0"
                    onClick={() => requestSort("branch")}
                  >
                    BRANCH {getSortIcon("branch")}
                  </th>
                  <th
                    className="py-3 border-0"
                    onClick={() => requestSort("company")}
                  >
                    COMPANY {getSortIcon("company")}
                  </th>
                  <th className="py-3 border-0 text-center">STATUS</th>
                  <th className="py-3 border-0 text-center">LEAVE STATUS</th>
                  <th
                    className="py-3 border-0"
                    onClick={() => requestSort("createdAt")}
                  >
                    CREATED AT {getSortIcon("createdAt")}
                  </th>
                  <th className="py-3 pe-4 border-0 text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody style={{ borderTop: "0" }}>
                {currentData.map((emp) => {
                  const sStyle = getStatusStyle(emp.employeeStatus);
                  return (
                    <tr key={emp.id} className="align-middle">
                      <td className="ps-4 py-3">
                        <div className="emp-name-text">
                          {emp.firstName} {emp.lastName}
                        </div>
                        <div className="emp-sub-text">
                          ID: {emp.employeeId}-{emp.branch?.BranchCode || "HQ"}
                          <br />
                          {emp.companyEmail || "-"}
                        </div>
                      </td>
                      <td className="table-cell-text">
                        {emp.department?.departmentName || "-"}
                      </td>
                      <td className="table-cell-text">
                        {emp.branch?.branchName || "-"}
                      </td>
                      <td className="table-cell-text">
                        {emp.company?.companyName || "-"}
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge rounded-pill ${sStyle.bg} bg-opacity-10 ${sStyle.text} border ${sStyle.border} border-opacity-25 badge-custom`}
                        >
                          {toCamelCase(emp.employeeStatus)}
                        </span>
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge rounded-pill ${sStyle.bg} bg-opacity-10 ${sStyle.text} border ${sStyle.border} border-opacity-25 badge-custom`}
                        >
                          {toCamelCase(emp.leaveStatus)}
                        </span>
                      </td>
                      <td className="table-cell-text">
                        {emp.createdAt
                          ? new Date(emp.createdAt).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm btn-outline-primary fw-bold"
                          style={{ fontSize: "0.75rem", borderRadius: "6px" }}
                          onClick={() => navigate(`/employee/${emp.id}`)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {currentData.length === 0 && (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center py-5 text-muted small"
                    >
                      No employees found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

  <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 p-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                Show
              </span>
              <select
                className="items-select"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[10, 25, 50].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                of <strong>{(totalItems || 0).toLocaleString()}</strong> entries
              </span>
            </div>

            <nav className="d-flex align-items-center">
              {/* Previous Button */}
              <button
                type="button"
                className="page-link-custom"
                onClick={() => {
                  const prevNum = Number(currentPage) - 1;
                  if (prevNum >= 1) handlePageChange(prevNum);
                }}
                disabled={Number(currentPage) <= 1}
                style={{
                  opacity: Number(currentPage) <= 1 ? 0.35 : 1,
                  cursor: Number(currentPage) <= 1 ? "not-allowed" : "pointer",
                }}
              >
                <i className="bi bi-chevron-left" style={{ fontSize: "0.8rem" }}></i>
              </button>

              {/* Dynamic Page Numbers Generation */}
              {(() => {
                const current = Number(currentPage);
                const pages = [];
                const maxVisiblePages = 5;

                let startPage = Math.max(1, current - Math.floor(maxVisiblePages / 2));
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }

                // Always show page 1
                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      type="button"
                      className={`page-link-custom ${current === 1 ? "active" : ""}`}
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </button>
                  );
                  if (startPage > 2) {
                    pages.push(
                      <span key="dots-start" className="px-2 text-muted" style={{ fontSize: "0.75rem" }}>
                        ...
                      </span>
                    );
                  }
                }

                // Render middle pages
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      type="button"
                      className={`page-link-custom ${current === i ? "active" : ""}`}
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </button>
                  );
                }

                // Always show last page
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="dots-end" className="px-2 text-muted" style={{ fontSize: "0.75rem" }}>
                        ...
                      </span>
                    );
                  }
                  pages.push(
                    <button
                      key={totalPages}
                      type="button"
                      className={`page-link-custom ${current === totalPages ? "active" : ""}`}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </button>
                  );
                }

                return pages;
              })()}

              {/* Next Button */}
              <button
                type="button"
                className="page-link-custom"
                onClick={() => {
                  const nextNum = Number(currentPage) + 1;
                  if (nextNum <= totalPages) handlePageChange(nextNum);
                }}
                disabled={Number(currentPage) >= totalPages}
                style={{
                  opacity: Number(currentPage) >= totalPages ? 0.35 : 1,
                  cursor: Number(currentPage) >= totalPages ? "not-allowed" : "pointer",
                }}
              >
                <i className="bi bi-chevron-right" style={{ fontSize: "0.8rem" }}></i>
              </button>
            </nav>
          </div>
        </div>
      </div>

      <EmployeeUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        modules={["employees", "contacts", "addresses"]}
        companyId={selectedCompanyId}
        setCompanyId={setSelectedCompanyId}
        preview={preview}
        setPreview={setPreview}
        onUpload={handleImport}
        companyList={allOrgs} 
      />
    </div>
  );
};

export default EmployeeTable;